import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import {
  Box,
  Typography,
  Toolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import EmailSettings from './Email/EmailSettings';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { listCustomers } from '../user/api/actions/query';
import axios from 'axios';
import { API_URL } from '../../setup/config/env';
import BrandedLoading from '../common/BrandedLoading';
import EmptyMessage from '../common/EmptyMessage';
import Pagination from '../common/Pagination';
import SectionPaper from '../common/SectionPaper';
import Image from '../common/Image';
import { getImageSource } from '../user/api/routes';
import dayjs from 'dayjs';
import routes from '../../setup/routes';
import { checkPermission } from '../common/Sidebar/checkPermission';

const styles = theme => ({
  container: {
    padding: '24px',
  },
  tableHeader: {
    backgroundColor: '#F8F9FE',
    '& th': {
      fontWeight: 700,
      color: '#9CA3AF',
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '1px',
    }
  },
  row: {
    '&:hover': {
      backgroundColor: '#F8F9FE',
    }
  }
});

class Subscribers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      subscribers: [],
      count: 0,
      page: 1,
      searchQuery: '',
      activeTab: 0,
      permissions: { canView: false, canEdit: false, canCreate: false, canDelete: false },
    };
  }

  componentDidMount() {
    this.verifyPermissions();
    this.fetchSubscribers();
  }

  verifyPermissions = async () => {
    const permissions = await checkPermission("marketing", "newsletter");
    this.setState({ permissions });
  };

  fetchSubscribers = async (page = 1) => {
    const { searchQuery } = this.state;
    this.setState({ isLoading: true });

    try {
      const response = await axios.get(`${API_URL}/api/cms/subscribers`, {
        params: {
          page,
          search: searchQuery,
          limit: 10
        }
      });

      if (response.data && response.data.success) {
        this.setState({
          subscribers: response.data.data.list,
          count: response.data.data.count,
          isLoading: false,
          page
        });
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      this.setState({ isLoading: false });
    }
  };

  handleSearch = (e) => {
    this.setState({ searchQuery: e.target.value, page: 1 }, () => {
      this.fetchSubscribers(1);
    });
  };

  handlePageChange = (page) => {
    this.fetchSubscribers(page);
  };

  handleToggleSubscription = async (subscriber) => {
    const action = subscriber.emailSubscription ? 'unsubscribe' : 'subscribe';
    try {
      const response = await axios.post(`${API_URL}/api/cms/${action}/${subscriber._id}?context=NewsletterSubscriber`);
      if (response.data && response.data.success) {
        const updatedSubscribers = this.state.subscribers.map(sub => 
          sub._id === subscriber._id ? { ...sub, emailSubscription: !sub.emailSubscription } : sub
        );
        this.setState({ subscribers: updatedSubscribers });
      }
    } catch (error) {
      console.error(`Error ${action}ing subscriber:`, error);
    }
  };

  render() {
    const { classes } = this.props;
    const { isLoading, subscribers, count, page, searchQuery, permissions } = this.state;

    return (
      <Box className={classes.container}>
        <Toolbar sx={{ px: '0 !important', mb: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1F2937' }}>
              Newsletter Subscribers
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
              Manage your audience and marketing subscriptions.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search subscribers..."
              value={searchQuery}
              onChange={this.handleSearch}
              sx={{ width: 250, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Newsletter Settings">
              <span>
                <IconButton 
                  onClick={() => this.props.history.push('/newsletter-settings')}
                  sx={{ color: '#0d3559' }}
                  disabled={!permissions.canEdit}
                >
                  <SettingsIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Toolbar>

        <SectionPaper>
          {isLoading ? (
            <BrandedLoading />
          ) : subscribers.length === 0 ? (
            <EmptyMessage message={'No subscribers found.'} />
          ) : (
            <>
              <Table>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Subscriber</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Joined On</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscribers.map((sub) => (
                    <TableRow key={sub._id} className={classes.row}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Image src={getImageSource(sub.image)} defaultSrc={getImageSource()} size={36} />
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1F2937' }}>
                            {sub.email.split('@')[0]}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#4B5563', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, opacity: 0.5 }} />
                          {sub.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sub.emailSubscription ? "SUBSCRIBED" : "UNSUBSCRIBED"}
                          size="small"
                          sx={{ 
                            bgcolor: sub.emailSubscription ? 'rgba(45, 206, 137, 0.1)' : 'rgba(245, 54, 92, 0.1)', 
                            color: sub.emailSubscription ? '#2dce89' : '#f5365c', 
                            fontWeight: 800, 
                            fontSize: '0.65rem' 
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: '#8898AA', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 12 }} />
                          {dayjs(sub.createdAt).format('MMM DD, YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#525F7F', textTransform: 'uppercase' }}>
                          {sub.channel || 'NEWSLETTER'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={sub.emailSubscription ? "Unsubscribe" : "Subscribe"}>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => this.handleToggleSubscription(sub)}
                              sx={{ color: sub.emailSubscription ? '#f5365c' : '#2dce89' }}
                              disabled={!permissions.canEdit}
                            >
                              {sub.emailSubscription ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {count > 10 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={count}
                    route={routes.subscribers}
                    rowsPerPage={10}
                    page={page}
                    onChange={this.handlePageChange}
                  />
                </Box>
              )}
            </>
          )}
        </SectionPaper></Box>
    );
  }
}

export default connect(null, { listCustomers })(withStyles(styles)(Subscribers));
